import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The 10-second architecture map: one transfer's journey through SafeFDS, playable.
 *
 * Accuracy contract (checked against the actual code, not a marketing diagram):
 * - the node order mirrors `decide_transaction.py`: API -> FeatureEngine -> Predictor ->
 *   PolicyEngine -> (mid band only) Governor -> StepUp -> one PostgreSQL transaction ->
 *   response;
 * - the governor is consulted BEFORE step-up availability ("authority before
 *   availability", f9f5449), and a demotion only revokes the mid band - outer bands are
 *   untouched, which the suspended scenario's copy states precisely;
 * - every failure scenario ends in REVIEW except a storage failure, which produces no
 *   decision at all and never fakes success;
 * - BLOCK does not exist as an automated outcome and therefore does not appear.
 *
 * Motion: one token walks the path once per scenario (~6s). With
 * prefers-reduced-motion - or where matchMedia is unavailable (jsdom) - the final path
 * renders immediately, which also makes tests assert end states instead of timings.
 */

type NodeId =
  | "arrive"
  | "gate"
  | "clues"
  | "score"
  | "decide"
  | "licence"
  | "stepup"
  | "save"
  | "respond";

const NODES: { id: NodeId; icon: string; label: string; tech: string; desc: string }[] = [
  {
    id: "arrive",
    icon: "💸",
    label: "송금 도착",
    tech: "Browser / Public Demo UI",
    desc: "사용자가 선택한 송금을 서버로 보냅니다.",
  },
  {
    id: "gate",
    icon: "🚪",
    label: "송금을 받아들이는 문",
    tech: "FastAPI Decision API",
    desc: "입력 형식을 확인하고 고유한 판정 요청을 만듭니다.",
  },
  {
    id: "clues",
    icon: "🔎",
    label: "단서 모으기",
    tech: "Feature Engine",
    desc: "금액, 시간, 처음 보내는 사람인지 같은 단서를 계산합니다.",
  },
  {
    id: "score",
    icon: "🤖",
    label: "위험 점수 매기기",
    tech: "Predictor",
    desc: "모은 단서로 0에서 1 사이의 위험 점수를 계산합니다.",
  },
  {
    id: "decide",
    icon: "⚖️",
    label: "어디로 보낼지 결정",
    tech: "Policy Engine",
    desc: "위험이 낮으면 통과, 애매하면 본인확인, 높으면 사람 검토.",
  },
  {
    id: "licence",
    icon: "🪪",
    label: "AI의 면허 확인",
    tech: "Runtime Autonomy Governor",
    desc: "자동 본인확인을 실행해도 되는 상태인지 먼저 확인합니다.",
  },
  {
    id: "stepup",
    icon: "📱",
    label: "본인 맞는지 확인",
    tech: "Verification / STEP_UP",
    desc: "통과하면 승인, 실패·시간초과·미연결이면 사람에게.",
  },
  {
    id: "save",
    icon: "🗄️",
    label: "판정 블랙박스",
    tech: "PostgreSQL Unit of Work",
    desc: "판정·검토 건·감사 기록·발송 대기함을 한 트랜잭션으로 저장.",
  },
  {
    id: "respond",
    icon: "✅",
    label: "결과 전달",
    tech: "Decision Response",
    desc: "결과와 모델·Feature·Policy 버전을 함께 돌려줍니다.",
  },
];

interface Scenario {
  id: string;
  label: string;
  /** Node ids the token visits, in order. */
  path: NodeId[];
  /** Node where things go wrong (rendered with the failure style), if any. */
  failsAt?: NodeId;
  /** Node that is deliberately skipped (rendered dimmed with a note), if any. */
  skips?: NodeId;
  terminal: { tone: "approve" | "review" | "error"; text: string };
  message: string;
  techNote: string;
}

const SCENARIOS: Scenario[] = [
  {
    id: "normal",
    label: "✅ 정상일 때",
    path: [
      "arrive",
      "gate",
      "clues",
      "score",
      "decide",
      "licence",
      "stepup",
      "save",
      "respond",
    ],
    terminal: { tone: "approve", text: "✅ 본인확인 통과 → 승인" },
    message:
      "중간 위험 송금의 여행입니다. 면허 확인을 거쳐 자동 본인확인을 통과하고, 저장까지 마친 뒤 결과가 전달됩니다. (위험이 낮으면 본인확인 없이 통과, 높으면 처음부터 사람 검토로 갑니다.)",
    techNote: "mid band: Governor 확인 → STEP_UP → APPROVE(reason: STEP_UP_PASSED)",
  },
  {
    id: "model-down",
    label: "🤖 모델이 고장 나면",
    path: ["arrive", "gate", "clues", "score", "save", "respond"],
    failsAt: "score",
    terminal: { tone: "review", text: "🧑‍💼 사람 검토(REVIEW)로" },
    message: "위험 점수를 믿을 수 없어서 사람이 확인합니다.",
    techNote: "reason: MODEL_UNAVAILABLE · degraded=true — 자동 승인으로 절대 흘리지 않음",
  },
  {
    id: "stepup-fail",
    label: "📱 본인확인이 실패하면",
    path: ["arrive", "gate", "clues", "score", "decide", "licence", "stepup", "save", "respond"],
    failsAt: "stepup",
    terminal: { tone: "review", text: "🧑‍💼 사람 검토(REVIEW)로" },
    message: "본인확인을 마치지 못했으므로 사람이 확인합니다.",
    techNote: "실패·시간초과·서비스 미연결 전부 REVIEW — 자동 승인 경로 없음",
  },
  {
    id: "licence-suspended",
    label: "🪪 AI 면허가 정지되면",
    path: ["arrive", "gate", "clues", "score", "decide", "licence", "save", "respond"],
    failsAt: "licence",
    skips: "stepup",
    terminal: { tone: "review", text: "🧑‍💼 사람 검토(REVIEW)로" },
    message:
      "본인확인 기능은 작동할 수 있지만, 자동화 권한이 회수되어 실행하지 않습니다. (중간 위험대만 사람에게 가고, 낮은·높은 위험의 판정은 그대로입니다.)",
    techNote:
      "authority before availability — reason: STEP_UP_SUSPENDED. 강등은 sticky, 재승격은 사람만",
  },
  {
    id: "db-down",
    label: "🗄️ DB가 고장 나면",
    path: ["arrive", "gate", "clues", "score", "decide", "licence", "stepup", "save"],
    failsAt: "save",
    terminal: { tone: "error", text: "🔴 오류 반환 — 판정 기록 없음" },
    message: "판정을 저장하지 못했습니다. 성공한 척하지 않고 오류를 돌려줍니다.",
    techNote: "커밋 실패 시 성공 응답을 생성하지 않음 (HTTP 503) — 재시도해도 이중 판정 없음",
  },
];

const STEP_MS = 700;

function prefersReducedMotion(): boolean {
  // jsdom has no matchMedia; treating that as "reduced" renders final states
  // immediately, which is also what the tests need.
  if (typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

interface Props {
  onVerify?: () => void;
}

export function ArchitecturePlayground({ onVerify }: Props) {
  const [scenario, setScenario] = useState<Scenario>(SCENARIOS[0]);
  // Index into scenario.path of the token's current position; path.length = finished.
  const [step, setStep] = useState<number>(() =>
    prefersReducedMotion() ? SCENARIOS[0].path.length : 0,
  );
  const timer = useRef<number | null>(null);

  const play = useCallback((next: Scenario) => {
    if (timer.current !== null) window.clearInterval(timer.current);
    setScenario(next);
    if (prefersReducedMotion()) {
      setStep(next.path.length);
      return;
    }
    setStep(0);
    timer.current = window.setInterval(() => {
      setStep((current) => {
        if (current + 1 >= next.path.length) {
          if (timer.current !== null) window.clearInterval(timer.current);
          timer.current = null;
        }
        return current + 1;
      });
    }, STEP_MS);
  }, []);

  useEffect(() => {
    // One playback of the normal flow on arrival (skipped under reduced motion).
    if (!prefersReducedMotion()) play(SCENARIOS[0]);
    return () => {
      if (timer.current !== null) window.clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finished = step >= scenario.path.length;
  const visited = new Set(scenario.path.slice(0, Math.min(step + 1, scenario.path.length)));
  const currentNode = finished ? null : scenario.path[step];

  function nodeState(id: NodeId): string {
    if (scenario.skips === id && (visited.has("licence") || finished)) return "skipped";
    if (!visited.has(id) && !finished) return scenario.path.includes(id) ? "" : "off-path";
    if (!scenario.path.includes(id)) return "off-path";
    if (scenario.failsAt === id && (finished || visited.has(id))) return "failed";
    if (visited.has(id) || finished) return "visited";
    return "";
  }

  return (
    <section className="card" id="arch-map">
      <h2>🏗️ 송금 한 건이 지나가는 길</h2>
      <p>
        어려운 상자 이름부터 외울 필요 없어요. <strong>송금 하나를 따라가면 전체 구조가
        보입니다.</strong> 아래 고장 버튼도 눌러보세요 — 어느 부품이 멈춰도 &quot;그냥
        통과&quot; 쪽으로 넘어가지 않습니다.
      </p>

      <div className="arch-scenarios" role="group" aria-label="시나리오 고르기">
        {SCENARIOS.map((s) => (
          <button
            key={s.id}
            type="button"
            className="arch-scenario-btn"
            aria-pressed={scenario.id === s.id}
            onClick={() => play(s)}
          >
            {s.label}
          </button>
        ))}
        <button type="button" className="arch-scenario-btn replay" onClick={() => play(scenario)}>
          🔁 다시 보기
        </button>
      </div>

      <ol className="arch-rail">
        {NODES.map((node) => (
          <li key={node.id} className={`arch-node ${nodeState(node.id)}`}>
            <span className="arch-node-head">
              <span className="arch-node-icon" aria-hidden="true">
                {node.icon}
              </span>
              <strong>{node.label}</strong>
              {currentNode === node.id && (
                <span className="arch-token" aria-hidden="true">
                  💸
                </span>
              )}
            </span>
            <span className="arch-node-tech">{node.tech}</span>
            <span className="arch-node-desc">{node.desc}</span>
            {scenario.skips === node.id && nodeState(node.id) === "skipped" && (
              <span className="arch-node-note">실행 안 함 (권한 회수됨)</span>
            )}
          </li>
        ))}
      </ol>

      <div aria-live="polite">
        {finished && (
          <div className={`arch-outcome ${scenario.terminal.tone}`}>
            <strong>{scenario.terminal.text}</strong>
            <p>{scenario.message}</p>
            <p className="muted arch-technote">🔬 기술 상세: {scenario.techNote}</p>
          </div>
        )}
      </div>

      <p className="muted">
        위 그림은 실제 코드의 실행 순서를 쉽게 풀어쓴 구조도입니다. 실제 서버 판정과 DB
        재조회는 &quot;직접 해보기&quot; 탭에서 확인할 수 있습니다.
      </p>

      <div className="arch-principles">
        <div className="arch-principle">
          <strong>🧑‍💼 고장 나면 사람에게</strong>
          <p>모델·단서 계산·본인확인이 실패해도 자동 승인하지 않습니다.</p>
        </div>
        <div className="arch-principle">
          <strong>🗄️ 저장 전에는 성공이 아님</strong>
          <p>PostgreSQL 커밋에 실패하면 성공한 판정을 만들어내지 않습니다.</p>
        </div>
        <div className="arch-principle">
          <strong>🧾 판정의 재료를 함께 기록</strong>
          <p>모델·Feature·Policy 버전과 감사 기록을 남깁니다.</p>
        </div>
      </div>

      <details className="fine-print">
        <summary>🧑‍💻 조금 더 깊이 (기술 메모 4줄)</summary>
        <ul>
          <li>
            <strong>같은 송금 요청이 실수로 두 번 도착해도, 판정은 한 번만 내려집니다.</strong>{" "}
            이 성질을 <strong>멱등성</strong>이라고 해요 — 같은 요청을 몇 번 보내도 결과는
            한 번 보낸 것과 같다는 뜻. 최종 안전장치는 DB의 고유 제약이라, 두 번째 요청은
            새로 판정하지 않고 저장해 둔 첫 판정을 그대로 돌려줍니다.
          </li>
          <li>
            <strong>기록 4가지(판정·검토 케이스·감사 기록·발송 대기함)는 한 묶음으로
            저장</strong>돼요 (한 트랜잭션) — 절반만 저장되고 나머지가 사라지는 일이
            없습니다.
          </li>
          <li>
            <strong>AI 면허가 정지되면 &quot;자동 본인확인&quot; 권한 하나만 회수</strong>
            됩니다 — 낮은 위험은 계속 승인되고, 높은 위험은 계속 사람에게 가요. (기록에는
            STEP_UP_SUSPENDED로 구분 저장)
          </li>
          <li>
            <strong>서버의 기본값은 규칙 스코어러</strong> — ML 모델은 스위치를 직접 켜야만
            돌아갑니다. 지금 배포는 ML로 서빙 중이에요.
          </li>
        </ul>
      </details>

      {onVerify && (
        <div className="cta-row">
          <button type="button" className="cta-button" onClick={onVerify}>
            이 구조가 실제로 움직이는지 확인하기 →
          </button>
        </div>
      )}
    </section>
  );
}
